/**
 * EquipmentForm - Reusable form for creating/editing equipment
 *
 * Tabbed dialog with sections:
 * - Basic Info
 * - Technical Specs
 * - DC Specifications
 * - Pricing
 * - Procurement
 * - Documentation
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    TextField,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Alert
} from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

// Equipment type to field definitions
const EQUIPMENT_FIELDS = {
    solar: {
        technical: [
            { name: 'wattage_per_panel', label: 'Wattage per Panel (W)', type: 'number' },
            { name: 'efficiency_percent', label: 'Efficiency (%)', type: 'number' },
            { name: 'panel_dimensions', label: 'Dimensions', type: 'text' },
            { name: 'panel_weight_kg', label: 'Weight (kg)', type: 'number' },
            { name: 'degradation_rate_annual', label: 'Annual Degradation (%)', type: 'number' }
        ],
        dc: [
            { name: 'dc_voltage_output_range', label: 'DC Voltage Output Range', type: 'text', placeholder: '600-1000V' },
            { name: 'dc_power_output_stc', label: 'DC Power Output STC (W)', type: 'number' },
            { name: 'max_system_voltage', label: 'Max System Voltage (V)', type: 'number' },
            { name: 'dc_current_output', label: 'DC Current Output (A)', type: 'number' }
        ],
        pricing: [
            { name: 'turnkey_cost_per_kw', label: 'Turnkey Cost ($/kW)', type: 'number', required: true }
        ]
    },
    bess: {
        technical: [
            { name: 'power_kw', label: 'Power (kW)', type: 'number', required: true },
            { name: 'capacity_kwh', label: 'Capacity (kWh)', type: 'number', required: true },
            { name: 'chemistry', label: 'Chemistry', type: 'select', options: ['LFP', 'NMC', 'Iron-Flow', 'Other'] },
            { name: 'roundtrip_efficiency', label: 'Roundtrip Efficiency (0-1)', type: 'number', step: 0.01 },
            { name: 'cycle_life', label: 'Cycle Life', type: 'number' },
            { name: 'cooling_type', label: 'Cooling Type', type: 'select', options: ['Air', 'Liquid', 'Passive'] },
            { name: 'dimensions', label: 'Dimensions', type: 'text' },
            { name: 'weight_kg', label: 'Weight (kg)', type: 'number' }
        ],
        dc: [
            { name: 'dc_voltage_range', label: 'DC Voltage Range', type: 'text', placeholder: '650-750V', hint: 'Must include 700V for Vortx compatibility' },
            { name: 'dc_power_rating_kw', label: 'DC Power Rating (kW)', type: 'number' },
            { name: 'has_integrated_inverter', label: 'Has Integrated Inverter', type: 'checkbox' },
            { name: 'dc_coupling_capable', label: 'DC Coupling Capable', type: 'checkbox' }
        ],
        pricing: [
            { name: 'equipment_cost', label: 'Equipment Cost ($)', type: 'number', required: true },
            { name: 'installation_cost', label: 'Installation Cost ($)', type: 'number', required: true }
        ]
    },
    generator: {
        technical: [
            { name: 'power_kw', label: 'Power (kW)', type: 'number', required: true },
            { name: 'fuel_type', label: 'Fuel Type', type: 'select', options: ['diesel', 'natural_gas', 'dual_fuel'], required: true },
            { name: 'fuel_efficiency_kwh_per_gal', label: 'Fuel Efficiency (kWh/gal)', type: 'number' },
            { name: 'fuel_efficiency_kwh_per_mmbtu', label: 'Fuel Efficiency (kWh/MMBtu)', type: 'number' },
            { name: 'emissions_tier', label: 'Emissions Tier', type: 'text', placeholder: 'EPA Tier 4 Final' },
            { name: 'noise_level_db', label: 'Noise Level (dB)', type: 'number' },
            { name: 'dimensions', label: 'Dimensions', type: 'text' },
            { name: 'weight_kg', label: 'Weight (kg)', type: 'number' }
        ],
        pricing: [
            { name: 'equipment_cost', label: 'Equipment Cost ($)', type: 'number', required: true },
            { name: 'installation_cost', label: 'Installation Cost ($)', type: 'number', required: true }
        ]
    },
    switchgear: {
        technical: [
            { name: 'rated_voltage', label: 'Rated Voltage', type: 'text', placeholder: '480V' },
            { name: 'rated_current', label: 'Rated Current (A)', type: 'number' },
            { name: 'breaker_type', label: 'Breaker Type', type: 'text', placeholder: 'Molded Case' },
            { name: 'arc_flash_rating', label: 'Arc Flash Rating', type: 'text', placeholder: 'ATPV 40 cal/cm²' },
            { name: 'dimensions', label: 'Dimensions', type: 'text' },
            { name: 'weight_kg', label: 'Weight (kg)', type: 'number' }
        ],
        pricing: [
            { name: 'equipment_cost', label: 'Equipment Cost ($)', type: 'number', required: true },
            { name: 'installation_cost', label: 'Installation Cost ($)', type: 'number', required: true }
        ]
    },
    inverter: {
        technical: [
            { name: 'ac_output_power_kw', label: 'AC Output Power (kW)', type: 'number', required: true },
            { name: 'ac_output_voltage', label: 'AC Output Voltage', type: 'text', placeholder: '480V 3-phase' },
            { name: 'conversion_efficiency', label: 'Conversion Efficiency (0-1)', type: 'number', step: 0.01 },
            { name: 'inverter_type', label: 'Inverter Type', type: 'select', options: ['string', 'central', 'microinverter'] },
            { name: 'coupling_type', label: 'Coupling Type', type: 'select', options: ['dc_coupled', 'ac_coupled'] },
            { name: 'dimensions', label: 'Dimensions', type: 'text' },
            { name: 'weight_kg', label: 'Weight (kg)', type: 'number' }
        ],
        dc: [
            { name: 'dc_input_voltage_range', label: 'DC Input Voltage Range', type: 'text', placeholder: '650-850V' },
            { name: 'dc_voltage_range', label: 'DC Voltage Range', type: 'text', placeholder: '650-750V', hint: 'Must include 700V' },
            { name: 'dc_input_current_max', label: 'DC Input Current Max (A)', type: 'number' }
        ],
        pricing: [
            { name: 'equipment_cost', label: 'Equipment Cost ($)', type: 'number', required: true },
            { name: 'installation_cost', label: 'Installation Cost ($)', type: 'number', required: true }
        ]
    },
    ev_charger: {
        technical: [
            { name: 'charger_type', label: 'Charger Type', type: 'select', options: ['Level_2_AC', 'DC_Fast_Charge'], required: true },
            { name: 'power_output_kw', label: 'Power Output (kW)', type: 'number', required: true },
            { name: 'simultaneous_ports', label: 'Simultaneous Ports', type: 'number' },
            { name: 'connector_types', label: 'Connector Types', type: 'multiselect', options: ['CCS', 'CHAdeMO', 'Tesla', 'J1772'] },
            { name: 'dimensions', label: 'Dimensions', type: 'text' },
            { name: 'weight_kg', label: 'Weight (kg)', type: 'number' }
        ],
        dc: [
            { name: 'dc_output_voltage_range', label: 'DC Output Voltage Range', type: 'text', placeholder: '200-920V' },
            { name: 'dc_output_current_max', label: 'DC Output Current Max (A)', type: 'number' },
            { name: 'dc_voltage_range', label: 'DC Voltage Range (bus)', type: 'text', placeholder: '650-750V' }
        ],
        pricing: [
            { name: 'equipment_cost', label: 'Equipment Cost ($)', type: 'number', required: true },
            { name: 'installation_cost', label: 'Installation Cost ($)', type: 'number', required: true }
        ]
    }
};

function EquipmentForm({ open, type, equipment, onSave, onCancel }) {
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (equipment) {
            setFormData(equipment);
        } else {
            // Initialize with defaults
            setFormData({
                status: 'active',
                warranty_years: 10
            });
        }
    }, [equipment]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        // Required common fields
        if (!formData.manufacturer?.trim()) newErrors.manufacturer = 'Required';
        if (!formData.model?.trim()) newErrors.model = 'Required';
        if (!formData.sku?.trim()) newErrors.sku = 'Required';

        // Type-specific required fields
        const fields = EQUIPMENT_FIELDS[type];
        ['technical', 'dc', 'pricing'].forEach(section => {
            fields[section]?.forEach(field => {
                if (field.required && !formData[field.name]) {
                    newErrors[field.name] = 'Required';
                }
            });
        });

        // DC voltage range validation
        if (formData.dc_voltage_range) {
            try {
                const range = formData.dc_voltage_range.replace('V', '').trim();
                const [min, max] = range.split('-').map(Number);
                if (!(min <= 700 && 700 <= max)) {
                    newErrors.dc_voltage_range = 'Range must include 700V';
                }
            } catch {
                newErrors.dc_voltage_range = 'Invalid format (use: 650-750V)';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validate()) {
            onSave(formData);
        }
    };

    const renderField = (field) => {
        if (field.type === 'select') {
            return (
                <FormControl fullWidth error={!!errors[field.name]}>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                        value={formData[field.name] || ''}
                        label={field.label}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                    >
                        {field.options.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        if (field.type === 'checkbox') {
            return (
                <FormControl fullWidth>
                    <label>
                        <input
                            type="checkbox"
                            checked={formData[field.name] || false}
                            onChange={(e) => handleChange(field.name, e.target.checked)}
                        />
                        {' '}{field.label}
                    </label>
                </FormControl>
            );
        }

        if (field.type === 'multiselect') {
            return (
                <FormControl fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                        multiple
                        value={formData[field.name] || []}
                        label={field.label}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map(value => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                            </Box>
                        )}
                    >
                        {field.options.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        return (
            <TextField
                fullWidth
                label={field.label}
                type={field.type}
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                error={!!errors[field.name]}
                helperText={errors[field.name] || field.hint}
                inputProps={{ step: field.step }}
                required={field.required}
            />
        );
    };

    const fields = EQUIPMENT_FIELDS[type] || {};

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { bgcolor: vortxColors.charcoal } }}
        >
            <DialogTitle sx={{ color: vortxColors.gold }}>
                {equipment ? 'Edit Equipment' : 'Create Equipment'}
            </DialogTitle>

            <DialogContent>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                    <Tab label="Basic Info" />
                    <Tab label="Technical" />
                    {fields.dc && <Tab label="DC Specs" />}
                    <Tab label="Pricing" />
                    <Tab label="Procurement" />
                    <Tab label="Documentation" />
                </Tabs>

                {/* Tab 0: Basic Info */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Manufacturer"
                                value={formData.manufacturer || ''}
                                onChange={(e) => handleChange('manufacturer', e.target.value)}
                                error={!!errors.manufacturer}
                                helperText={errors.manufacturer}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Model"
                                value={formData.model || ''}
                                onChange={(e) => handleChange('model', e.target.value)}
                                error={!!errors.model}
                                helperText={errors.model}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="SKU / Part Number"
                                value={formData.sku || ''}
                                onChange={(e) => handleChange('sku', e.target.value)}
                                error={!!errors.sku}
                                helperText={errors.sku}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={formData.status || 'active'}
                                    label="Status"
                                    onChange={(e) => handleChange('status', e.target.value)}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="discontinued">Discontinued</MenuItem>
                                    <MenuItem value="obsolete">Obsolete</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Warranty (years)"
                                type="number"
                                value={formData.warranty_years || ''}
                                onChange={(e) => handleChange('warranty_years', parseInt(e.target.value))}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 1: Technical Specs */}
                <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={2}>
                        {fields.technical?.map(field => (
                            <Grid item xs={12} sm={6} key={field.name}>
                                {renderField(field)}
                            </Grid>
                        ))}
                    </Grid>
                </TabPanel>

                {/* Tab 2: DC Specs (conditional) */}
                {fields.dc && (
                    <TabPanel value={tabValue} index={2}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Vortx DC bus voltage: 700V. Equipment must be compatible with this voltage.
                        </Alert>
                        <Grid container spacing={2}>
                            {fields.dc.map(field => (
                                <Grid item xs={12} sm={6} key={field.name}>
                                    {renderField(field)}
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>
                )}

                {/* Tab 3: Pricing */}
                <TabPanel value={tabValue} index={fields.dc ? 3 : 2}>
                    <Grid container spacing={2}>
                        {fields.pricing?.map(field => (
                            <Grid item xs={12} sm={6} key={field.name}>
                                {renderField(field)}
                            </Grid>
                        ))}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Quote Date"
                                type="date"
                                value={formData.last_quote_date || ''}
                                onChange={(e) => handleChange('last_quote_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Quote Reference Number"
                                value={formData.quote_reference_number || ''}
                                onChange={(e) => handleChange('quote_reference_number', e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 4: Procurement */}
                <TabPanel value={tabValue} index={fields.dc ? 4 : 3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Supplier Name"
                                value={formData.supplier_name || ''}
                                onChange={(e) => handleChange('supplier_name', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Supplier Contact"
                                value={formData.supplier_contact || ''}
                                onChange={(e) => handleChange('supplier_contact', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Lead Time (weeks)"
                                type="number"
                                value={formData.lead_time_weeks || ''}
                                onChange={(e) => handleChange('lead_time_weeks', parseInt(e.target.value))}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 5: Documentation */}
                <TabPanel value={tabValue} index={fields.dc ? 5 : 4}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Spec Sheet URL"
                                value={formData.spec_sheet_url || ''}
                                onChange={(e) => handleChange('spec_sheet_url', e.target.value)}
                                placeholder="https://..."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Image URL"
                                value={formData.image_url || ''}
                                onChange={(e) => handleChange('image_url', e.target.value)}
                                placeholder="https://..."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Certifications (comma-separated)"
                                value={(formData.certifications || []).join(', ')}
                                onChange={(e) => handleChange('certifications', e.target.value.split(',').map(s => s.trim()))}
                                placeholder="UL 1741, IEEE 1547"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Internal Notes"
                                value={formData.notes || ''}
                                onChange={(e) => handleChange('notes', e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>
            </DialogContent>

            <DialogActions>
                <Button onClick={onCancel} sx={{ color: vortxColors.stone }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{ bgcolor: vortxColors.gold, color: '#000' }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EquipmentForm;
